import { useCallback } from "react";
import { toast } from "sonner";

import { authApi } from "@/lib/api/auth";
import {
  DEFAULT_REPORTING_CURRENCY,
  REPORTING_CURRENCIES,
  REPORTING_CURRENCY_KEY,
  isCurrencyCode,
  parseReportingCurrency,
  type ReportingCurrency,
} from "@/lib/constants/userPreferences";
import { useAuthStore } from "@/store/authStore";

/**
 * The currency every cost surface reads totals in.
 *
 * One hook rather than each page reaching into preferences itself, because two
 * surfaces resolving the currency differently is the same class of bug as two
 * surfaces resolving a company's spend differently: the numbers stop agreeing
 * and nothing on screen says why.
 *
 * The value is a **display** choice, not a filter. Cost is stored pre-converted
 * into each supported currency, so switching re-reads a different column rather
 * than re-converting anything. The billed figure never changes.
 */
export function useReportingCurrency() {
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);

  // The server publishes what it can actually report in. Trusting the local
  // constant instead is how the two lists drift: offering a currency the
  // backend does not know produces a 400, and omitting one it does support
  // hides a working feature. The constant stays only as a fallback for a
  // backend too old to send the field.
  const supported =
    user?.reporting_currencies
      ?.map((code) => code.toUpperCase())
      .filter(isCurrencyCode) ??
    ([...REPORTING_CURRENCIES] as ReportingCurrency[]);

  const saved = parseReportingCurrency(user?.preferences);
  // A saved currency the server has since stopped supporting must not be sent.
  const currency = supported.includes(saved) ? saved : DEFAULT_REPORTING_CURRENCY;

  const setCurrency = useCallback(
    async (next: ReportingCurrency) => {
      if (!user || next === currency) return;

      const previous = user.preferences ?? {};
      const nextPrefs = { ...previous, [REPORTING_CURRENCY_KEY]: next };

      // Optimistic: the whole point of the control is that totals re-render at
      // once. Rolled back below if the write fails, so a failed save cannot
      // leave the UI showing a currency the profile does not have.
      setUser({ ...user, preferences: nextPrefs });

      try {
        const updated = await authApi.updateMe({ preferences: nextPrefs });
        const merged =
          updated.preferences != null && typeof updated.preferences === "object"
            ? (updated.preferences as Record<string, unknown>)
            : nextPrefs;
        setUser({ ...user, preferences: merged });
      } catch {
        setUser({ ...user, preferences: previous });
        toast.error("Could not save your currency preference.");
      }
    },
    [user, currency, setUser],
  );

  return { currency, setCurrency, supported };
}
